import React, { useEffect, useState } from 'react';
import { Box, Typography, styled, IconButton, TextField } from '@mui/material';
import ImageComponent from 'ui/components/shared/ImageComponent';
import { useTranslation } from 'react-i18next';
import { Delete, Search } from '@mui/icons-material';
import MobileSkillsDetailView from './MobileSkillsDetailView';

const MobileContainer = styled(Box)({
  width: '100%',
  height: '100vh',
  backgroundColor: '#f0f2f5',
  display: 'flex',
  flexDirection: 'column',
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0
});

const HeaderSection = styled(Box)({
  padding: '1.25rem 1.5rem 1rem 1.5rem',
  paddingTop: '5.5rem',
  background: 'linear-gradient(135deg, #ffffff 0%, rgba(255, 255, 255, 0.95) 50%, rgba(161, 183, 165, 0.1) 100%)',
  borderBottom: '1px solid rgba(161, 183, 165, 0.2)',
  boxShadow: '0 2px 16px rgba(161, 183, 165, 0.1)',
  position: 'relative',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.4) 100%)',
    backdropFilter: 'blur(20px)',
    zIndex: -1
  },
  
  '@media (max-width: 390px)': {
    padding: '1rem 1.25rem 0.75rem 1.25rem',
    paddingTop: '5rem',
  },
  
  '@media (max-width: 375px)': {
    padding: '0.875rem 1rem 0.625rem 1rem',
    paddingTop: '4.5rem',
  },
  
  '@media (max-width: 320px)': {
    padding: '0.75rem 0.875rem 0.5rem 0.875rem',
    paddingTop: '4rem',
  },
  
  '@media (max-width: 280px)': {
    padding: '0.5rem 0.75rem 0.375rem 0.75rem',
    paddingTop: '3.5rem',
  }
});

const ModernTitle = styled(Typography)({
  fontSize: '2.2rem',
  fontWeight: 800,
  background: 'linear-gradient(135deg, #1f2937 0%, #374151 50%, #A1B7A5 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginBottom: '1.25rem',
  letterSpacing: '-0.025em',
  textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  
  '@media (max-width: 390px)': {
    fontSize: '2rem',
    marginBottom: '1rem'
  },
  
  '@media (max-width: 375px)': {
    fontSize: '1.8rem',
    marginBottom: '0.875rem'
  },
  
  '@media (max-width: 320px)': {
    fontSize: '1.6rem',
    marginBottom: '0.75rem',
    letterSpacing: '-0.02em'
  },
  
  '@media (max-width: 280px)': {
    fontSize: '1.4rem',
    marginBottom: '0.625rem',
    letterSpacing: '-0.015em'
  }
});

const SearchField = styled(TextField)({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#f8f9fa',
    borderRadius: '2rem',
    fontSize: '0.95rem',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    border: '1px solid rgba(161, 183, 165, 0.2)',
    
    '& fieldset': {
      border: 'none'
    },
    
    '&:hover': {
      backgroundColor: '#ffffff',
      boxShadow: '0 4px 16px rgba(161, 183, 165, 0.15)',
      transform: 'translateY(-1px)'
    },
    
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      boxShadow: '0 6px 24px rgba(161, 183, 165, 0.2)',
      transform: 'translateY(-2px)',
      borderColor: 'rgba(161, 183, 165, 0.4)'
    }
  },
  
  '& .MuiInputBase-input': {
    padding: '0.875rem 1.25rem',
    '&::placeholder': {
      color: '#9ca3af',
      fontWeight: '400'
    }
  },
  
  '@media (max-width: 360px)': {
    '& .MuiInputBase-input': {
      padding: '0.75rem 1rem',
      fontSize: '0.9rem'
    }
  }
});

const ChatList = styled(Box)({
  flex: 1,
  overflow: 'auto',
  backgroundColor: '#f0f2f5',
  paddingBottom: '5rem',
  
  '&::-webkit-scrollbar': {
    display: 'none'
  },
  scrollbarWidth: 'none'
});

const ChatItem = styled(Box)({
  position: 'relative',
  overflow: 'hidden'
});

const SwipeableChat = styled(Box)<{ swiped?: boolean }>(({ swiped }) => ({
  backgroundColor: 'white',
  borderBottom: '1px solid rgba(161, 183, 165, 0.1)',
  transform: swiped ? 'translateX(-80px)' : 'translateX(0)',
  transition: 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  cursor: 'pointer',
  
  '&:active': {
    backgroundColor: '#f8f9fa'
  },
  
  '&:hover': {
    backgroundColor: '#fdfdfd'
  }
}));

const DeleteButton = styled(Box)({
  position: 'absolute',
  right: 0,
  top: 0,
  bottom: 0,
  width: '80px',
  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.2)'
});

const ChatContent = styled(Box)({
  padding: '1.25rem 1.5rem',
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
  minHeight: '85px',
  
  '@media (max-width: 390px)': {
    padding: '1rem 1.25rem',
    gap: '0.875rem',
    minHeight: '78px'
  },
  
  '@media (max-width: 375px)': {
    padding: '0.875rem 1rem',
    gap: '0.75rem',
    minHeight: '72px'
  },
  
  '@media (max-width: 320px)': {
    padding: '0.75rem 0.875rem',
    gap: '0.625rem',
    minHeight: '66px'
  },
  
  '@media (max-width: 280px)': {
    padding: '0.625rem 0.75rem',
    gap: '0.5rem',
    minHeight: '60px'
  }
});

const ProfileImageContainer = styled(Box)({
  width: '3.75rem',
  height: '3.75rem',
  borderRadius: '50%',
  overflow: 'hidden',
  flexShrink: 0,
  backgroundColor: '#e5e7eb',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '2px solid rgba(161, 183, 165, 0.15)',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s ease',
  
  '&:hover': {
    transform: 'scale(1.05)',
    borderColor: 'rgba(161, 183, 165, 0.3)',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)'
  },
  
  '@media (max-width: 390px)': {
    width: '3.5rem',
    height: '3.5rem'
  },
  
  '@media (max-width: 375px)': {
    width: '3.25rem',
    height: '3.25rem'
  },
  
  '@media (max-width: 320px)': {
    width: '3rem',
    height: '3rem',
    border: '1.5px solid rgba(161, 183, 165, 0.15)'
  },
  
  '@media (max-width: 280px)': {
    width: '2.75rem',
    height: '2.75rem',
    border: '1px solid rgba(161, 183, 165, 0.15)'
  }
});

const ChatInfo = styled(Box)({
  flex: 1,
  minWidth: 0,
  paddingRight: '0.5rem'
});

const ChatTitle = styled(Typography)({
  fontSize: '1.125rem',
  fontWeight: 600,
  color: '#111827',
  marginBottom: '0.375rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  letterSpacing: '0.01em',
  
  '@media (max-width: 390px)': {
    fontSize: '1.05rem',
    marginBottom: '0.3rem'
  },
  
  '@media (max-width: 375px)': {
    fontSize: '1rem',
    marginBottom: '0.25rem'
  },
  
  '@media (max-width: 320px)': {
    fontSize: '0.95rem',
    marginBottom: '0.2rem',
    letterSpacing: '0.005em'
  },
  
  '@media (max-width: 280px)': {
    fontSize: '0.9rem',
    marginBottom: '0.15rem',
    letterSpacing: '0'
  }
});

const LastMessage = styled(Typography)({
  fontSize: '0.9rem',
  color: '#6b7280',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  lineHeight: '1.4',
  fontWeight: '400',
  
  '@media (max-width: 390px)': {
    fontSize: '0.875rem'
  },
  
  '@media (max-width: 375px)': {
    fontSize: '0.85rem'
  },
  
  '@media (max-width: 320px)': {
    fontSize: '0.8rem',
    lineHeight: '1.3'
  },
  
  '@media (max-width: 280px)': {
    fontSize: '0.75rem',
    lineHeight: '1.2'
  }
});

const ChatMeta = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: '0.375rem',
  flexShrink: 0,
  minWidth: '60px',
  
  '@media (max-width: 360px)': {
    gap: '0.25rem',
    minWidth: '50px'
  }
});

const TimeStamp = styled(Typography)({
  fontSize: '0.8rem',
  color: '#9ca3af',
  fontWeight: '500',
  
  '@media (max-width: 390px)': {
    fontSize: '0.775rem'
  },
  
  '@media (max-width: 375px)': {
    fontSize: '0.75rem'
  },
  
  '@media (max-width: 320px)': {
    fontSize: '0.7rem'
  },
  
  '@media (max-width: 280px)': {
    fontSize: '0.65rem'
  }
});

const ProgressIndicator = styled(Box)<{ progress: number }>(({ progress }) => ({
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  backgroundColor: progress > 80 ? '#10b981' : progress > 40 ? '#f59e0b' : '#ef4444',
  boxShadow: `0 0 8px ${progress > 80 ? '#10b981' : progress > 40 ? '#f59e0b' : '#ef4444'}40`,
  
  '@media (max-width: 360px)': {
    width: '5px',
    height: '5px'
  }
}));

const getLocalTimestamp = (skillId: string): string | null => {
  const key = `courseActivity_${skillId}`;
  return localStorage.getItem(key);
};

const setLocalTimestamp = (skillId: string, timestamp: string) => {
  const key = `courseActivity_${skillId}`;
  localStorage.setItem(key, timestamp);
};

const getMostRecentTimestamp = (apiTimestamp: string | null, localTimestamp: string | null): string | null => {
  if (!apiTimestamp && !localTimestamp) return null;
  if (!apiTimestamp) return localTimestamp;
  if (!localTimestamp) return apiTimestamp;
  
  // Vergleiche Zeitstempel und gib den neueren zurück
  const apiDate = new Date(apiTimestamp).getTime();
  const localDate = new Date(localTimestamp).getTime();
  
  return apiDate > localDate ? apiTimestamp : localTimestamp;
};

interface ChatData {
  lastMessage?: string;
  lastMessageTime?: string;
  lastActivity?: string | null;
}


const SkillsListView = ({ active, setActive, options, deleteUserCourse, updateProgress }: any) => {
  const { t } = useTranslation()
  const [swipedItem, setSwipedItem] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [chatData, setChatData] = useState<Record<string, ChatData>>({})

  const decodeMessage = (message: string) => {
      const userName = localStorage.getItem('currentUserName')?.split(' ')[0] || '';
      const decodedMessage = decodeURIComponent(message);
    return decodedMessage.replaceAll('@user.name', userName);
    };



  
const processSkillsWithHybridTimestamps = (skills: any[]) => {
    return skills.map(skill => {
      const localTimestamp = getLocalTimestamp(skill.id);
      const apiTimestamp = skill.lastActivity;
      const mostRecentTimestamp = getMostRecentTimestamp(apiTimestamp, localTimestamp);
      
      return {
        ...skill,
        lastActivity: mostRecentTimestamp,
        // Markiere, ob der lokale Zeitstempel neuer ist
        isLocalNewer: localTimestamp && (!apiTimestamp || new Date(localTimestamp) > new Date(apiTimestamp))
      };
    });
  };

  // Event Listener Setup
  useEffect(() => {
    const handleCourseOpened = (event: any) => {
      const { skillId } = event.detail;
      const timestamp = new Date().toISOString();
      setLocalTimestamp(skillId, timestamp);
      
      // Aktualisiere lokale State sofort
      updateSkillTimestamp(skillId, timestamp);
    };

    const handleCourseClosed = (event: any) => {
      const { skillId } = event.detail;
      const timestamp = new Date().toISOString();
      setLocalTimestamp(skillId, timestamp);
      
      // Aktualisiere lokale State sofort
      updateSkillTimestamp(skillId, timestamp);
    };

    const handleChatUpdate = (event: any) => {
      const { skillId, chatPreview } = event.detail;
      const timestamp = new Date().toISOString();
      setLocalTimestamp(skillId, timestamp);
      
      setChatData(prev => ({
        ...prev,
        [skillId]: {
          lastMessage: truncateMessage(chatPreview.content),
          lastMessageTime: formatTimeStamp(timestamp),
          lastActivity: timestamp
        }
      }));
      
      // Aktualisiere auch Skill-Zeitstempel
      updateSkillTimestamp(skillId, timestamp);
    };

    const handleCourseCompleted = (event: any) => {
      const { skillId } = event.detail;
      const timestamp = new Date().toISOString();
      setLocalTimestamp(skillId, timestamp);
      
      setChatData(prev => ({
        ...prev,
        [skillId]: {
          ...prev[skillId],
          lastMessage: t('Kurs abgeschlossen'),
          lastMessageTime: formatTimeStamp(timestamp),
          lastActivity: timestamp
        }
      }));
      
      updateSkillTimestamp(skillId, timestamp);
    };

    // Event Listener registrieren
    window.addEventListener('courseOpened', handleCourseOpened);
    window.addEventListener('courseClosed', handleCourseClosed);
    window.addEventListener('chatMessageUpdate', handleChatUpdate);
    window.addEventListener('courseCompleted', handleCourseCompleted);
    
    return () => {
      window.removeEventListener('courseOpened', handleCourseOpened);
      window.removeEventListener('courseClosed', handleCourseClosed);
      window.removeEventListener('chatMessageUpdate', handleChatUpdate);
      window.removeEventListener('courseCompleted', handleCourseCompleted);
    };
  }, [t]);

  // Hilfsfunktion zum Aktualisieren von Skill-Zeitstempeln
  const updateSkillTimestamp = (skillId: string, timestamp: string) => {
    // Hier könnten Sie den options-Array aktualisieren, falls nötig
    // Da options von außen kommt, könnte ein Callback nötig sein
  };

  // Chat-Daten laden (hybride Lösung)
  const loadChatData = () => {
    const chatDataMap: Record<string, ChatData> = {};
    
    for (const skill of options) {
      const localTimestamp = getLocalTimestamp(skill.id);
      const apiTimestamp = skill.lastActivity;
      const mostRecentTimestamp = getMostRecentTimestamp(apiTimestamp, localTimestamp);
      
      try {
        const lastChatMessage = decodeMessage(String(localStorage.getItem(`lastChatMessage_${skill.id}`)));
        
        if (lastChatMessage) {
          const parsed = JSON.parse(lastChatMessage);
          const isCompleted = skill.progress >= 100;
          
          chatDataMap[skill.id] = {
            lastMessage: isCompleted ? t('Kurs abgeschlossen') : truncateMessage(parsed.content || parsed.message),
            lastMessageTime: formatTimeStamp(mostRecentTimestamp || parsed.timestamp),
            lastActivity: mostRecentTimestamp
          };
        } else {
          const chatHistory = localStorage.getItem(`chatBackup_${skill.id}`);
          
          if (chatHistory) {
            const parsed = JSON.parse(chatHistory);
            const { userResponse, gptResponse } = parsed;
            
            let lastMessage = '';
            if (gptResponse && gptResponse.trim()) {
              lastMessage = gptResponse.trim();
            } else if (userResponse && userResponse.trim()) {
              lastMessage = userResponse.trim();
            } else {
              lastMessage = t('Kurs bereit zum Lernen');
            }
            
            const isCompleted = skill.progress >= 100;
            
            chatDataMap[skill.id] = {
              lastMessage: isCompleted ? t('Kurs abgeschlossen') : truncateMessage(lastMessage),
              lastMessageTime: formatTimeStamp(mostRecentTimestamp || new Date().toISOString()),
              lastActivity: mostRecentTimestamp
            };
          } else {
            const isCompleted = skill.progress >= 100;
            
            chatDataMap[skill.id] = {
              lastMessage: isCompleted ? t('Kurs abgeschlossen') : t('Kurs bereit zum Lernen'),
              lastMessageTime: formatTimeStamp(mostRecentTimestamp || new Date().toISOString()),
              lastActivity: mostRecentTimestamp
            };
          }
        }
      } catch (error) {
        console.error('Error loading chat data for skill:', skill.id, error);
        const isCompleted = skill.progress >= 100;
        
        chatDataMap[skill.id] = {
          lastMessage: isCompleted ? t('Kurs abgeschlossen') : t('Kurs bereit zum Lernen'),
          lastMessageTime: formatTimeStamp(mostRecentTimestamp || new Date().toISOString()),
          lastActivity: mostRecentTimestamp
        };
      }
    }
    
    setChatData(chatDataMap);
  };

  useEffect(() => {
    if (options.length > 0) {
      loadChatData();
    }
  }, [options, t]);

  // Verarbeite Skills mit hybriden Zeitstempeln für die Sortierung
  const processedSkills = processSkillsWithHybridTimestamps(options);

  const filteredSkills = processedSkills
    .filter((skill: any) =>
      skill.title.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a: any, b: any) => {
      // Sortiere nach dem neuesten Zeitstempel (hybrid)
      if (!a.lastActivity && !b.lastActivity) return 0;
      if (!a.lastActivity) return 1;
      if (!b.lastActivity) return -1;
      return new Date(b.lastActivity).getTime() - new Date(a.lastActivity).getTime();
    });

  const handleSkillSelect = (skill: any) => {
    localStorage.setItem("lastBookedCourse", skill.id.toString());
    
    // Event für Kurs-Öffnung senden
    window.dispatchEvent(new CustomEvent('courseOpened', {
      detail: { skillId: skill.id }
    }));
    
    setActive(skill);
  };

  const handleSwipe = (skillId: string) => {
    setSwipedItem(swipedItem === skillId ? null : skillId);
  };

  const handleDelete = (skillId: string) => {
    deleteUserCourse(skillId);
    setSwipedItem(null);
    
    // Lösche auch lokale Zeitstempel
    const key = `courseActivity_${skillId}`;
    localStorage.removeItem(key);
  };

  const formatTimeStamp = (timestamp: string | null) => {
    if (!timestamp) {
      return new Date().toLocaleTimeString('de-DE', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false,
        timeZone: 'Europe/Berlin'
      });
    }
    
    try {
      const date = new Date(timestamp);
      const now = new Date();
      
      // Heute: Zeige Uhrzeit
      if (date.toDateString() === now.toDateString()) {
        return date.toLocaleTimeString('de-DE', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false,
          timeZone: 'Europe/Berlin'
        });
      }
      
      // Gestern
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      if (date.toDateString() === yesterday.toDateString()) {
        return t('Gestern');
      }
      
      // Diese Woche: Zeige Wochentag
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 7);
      if (date > weekAgo) {
        return date.toLocaleDateString('de-DE', { 
          weekday: 'short',
          timeZone: 'Europe/Berlin'
        });
      }
      
      // Älter: Zeige Datum
      return date.toLocaleDateString('de-DE', { 
        day: '2-digit', 
        month: '2-digit',
        timeZone: 'Europe/Berlin'
      });
    } catch (error) {
      return new Date().toLocaleTimeString('de-DE', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false,
        timeZone: 'Europe/Berlin'
      });
    }
  };

  const truncateMessage = (message: string, maxLength: number = 50) => {
    if (!message) return t('Kurs bereit zum Lernen');
    
    const cleanMessage = message.replace(/<[^>]*>/g, '').trim();
    
    if (cleanMessage.length <= maxLength) return cleanMessage;
    return cleanMessage.substring(0, maxLength) + '...';
  };

  return (
    <>
      <MobileContainer>
        <HeaderSection>
          <ModernTitle>{t('My Skills')}</ModernTitle>
          
          <SearchField
            placeholder={t('Kurse durchsuchen...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ color: '#9ca3af', mr: 1.25, fontSize: '1.3rem' }} />
            }}
          />
        </HeaderSection>

        <ChatList>
          {filteredSkills.map((skill: any) => {
            const skillChatData = chatData[skill.id] || {};
            
            return (
              <ChatItem key={skill.id}>
                <DeleteButton>
                  <IconButton 
                    onClick={() => handleDelete(skill.id)}
                    sx={{ color: 'white' }}
                  >
                    <Delete fontSize="large" />
                  </IconButton>
                </DeleteButton>

                <SwipeableChat 
                  swiped={swipedItem === skill.id}
                  onClick={() => handleSkillSelect(skill)}
                  onTouchStart={(e) => {
                    const startX = e.touches[0].clientX;
                    let moved = false;
                    
                    const handleTouchMove = (e: TouchEvent) => {
                      const currentX = e.touches[0].clientX;
                      const diffX = startX - currentX;
                      
                      if (diffX > 50 && !moved) {
                        handleSwipe(skill.id);
                        moved = true;
                      } else if (diffX < -50 && !moved) {
                        setSwipedItem(null);
                        moved = true;
                      }
                    };
                    
                    const handleTouchEnd = () => {
                      document.removeEventListener('touchmove', handleTouchMove);
                      document.removeEventListener('touchend', handleTouchEnd);
                    };
                    
                    document.addEventListener('touchmove', handleTouchMove);
                    document.addEventListener('touchend', handleTouchEnd);
                  }}
                >
                  <ChatContent>
                    <ProfileImageContainer>
                      <ImageComponent 
                        src={skill.thumbnail || skill.image} 
                        alt={skill.title}
                        width="100%"
                        height="100%"
                        objectFit
                      />
                    </ProfileImageContainer>

                    <ChatInfo>
                      <ChatTitle>
                        {skill.title}
                        {/* Optionale Anzeige wenn lokaler Zeitstempel neuer ist */}
                        {skill.isLocalNewer && (
                          <Typography 
                            component="span" 
                            sx={{ 
                              ml: 1, 
                              fontSize: '0.7rem', 
                              color: '#10b981',
                              fontWeight: 500
                            }}
                          >
                            •
                          </Typography>
                        )}
                      </ChatTitle>
                      <LastMessage>
                        {skillChatData.lastMessage || t('Kurs bereit zum Lernen')}
                      </LastMessage>
                    </ChatInfo>

                    <ChatMeta>
                      <TimeStamp>
                        {skillChatData.lastMessageTime || formatTimeStamp(skill.lastActivity)}
                      </TimeStamp>
                      <ProgressIndicator progress={skill.progress || 0} />
                    </ChatMeta>
                  </ChatContent>
                </SwipeableChat>
              </ChatItem>
            );
          })}

          {/* Empty State bleibt gleich */}
          {filteredSkills.length === 0 && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              padding: '4rem 2rem',
              textAlign: 'center',
              minHeight: '300px'
            }}>
              <Box sx={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: 'rgba(161, 183, 165, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1.5rem'
              }}>
                <Search sx={{ fontSize: '2rem', color: '#A1B7A5' }} />
              </Box>
              
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#374151', 
                  mb: 1,
                  fontSize: '1.25rem',
                  fontWeight: 600
                }}
              >
                {searchQuery ? t('Keine Kurse gefunden') : t('Noch keine Skills')}
              </Typography>
              
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#6b7280',
                  fontSize: '0.95rem',
                  lineHeight: 1.5,
                  maxWidth: '280px'
                }}
              >
                {searchQuery 
                  ? t('Versuche einen anderen Suchbegriff oder durchstöbere alle verfügbaren Kurse') 
                  : t('Starte dein Lernabenteuer, indem du deinen ersten Skill hinzufügst')
                }
              </Typography>
              
              {!searchQuery && (
                <Box 
                  sx={{
                    mt: 2,
                    px: 3,
                    py: 1.5,
                    backgroundColor: 'rgba(161, 183, 165, 0.1)',
                    borderRadius: '12px',
                    border: '1px solid rgba(161, 183, 165, 0.2)'
                  }}
                >
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: '#A1B7A5',
                      fontSize: '0.8rem',
                      fontWeight: 500
                    }}
                  >
                    {t('Gehe zum Skillary um Kurse zu entdecken')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </ChatList>
      </MobileContainer>

      {active && active.title && (
        <MobileSkillsDetailView 
          skill={active}
          onClose={() => setActive({ title: "", thumbnail: "" })}
          onDelete={() => deleteUserCourse(active.id)}
          updateProgress={updateProgress}
        />
      )}
    </>
  );
};

export default SkillsListView;